import React, { useEffect, useState, memo } from 'react';
import { useMEMTranslation } from '../../utils/text-utils';
import { useTrackInteraction } from '../../services/logging/hooks';
import { usePageContext } from '../../utils/page-context';
import { useAvailablePerformancePartitions } from './hooks';
import { Option, Combobox } from '../../providers/harmony-scope/lazy-enablers';
import { useMonthDayYear, getFormattedMonthYear } from '../../utils/date-utils';
import { EPartitionCadence, TPartition } from '../../ducks/filters/types-filters';

interface IProps {
  selectedParticipation: string;
  selectedCadence: EPartitionCadence;
  selectedPartition: string;
  selectedPartitionGroup: string;
  onChange: (partition: string) => void;
}
const formatPartition = (monthDayYear: (date: Date | string) => string, partition: TPartition, cadence?: EPartitionCadence) => {
  if (!partition) return '';
  // If cadence is monthly, add a comma after the month name in the title
  if (cadence === EPartitionCadence.Monthly) {
    return `${getFormattedMonthYear(partition.title)} (${monthDayYear(partition.startDate)} - ${monthDayYear(partition.endDate)})`;
  }
  return `${partition.title} (${monthDayYear(partition.startDate)} - ${monthDayYear(partition.endDate)})`;
};

const componentName = 'Performance Partition Picker Filters Panel';
export const ComponentFilterPerformancePartition = memo((props: IProps) => {
  // Props Accessors
  const {
    selectedParticipation: selectedParticipationProp,
    selectedCadence: selectedCadenceProp,
    selectedPartition: selectedPartitionProp,
    selectedPartitionGroup: selectedPartitionGroupProp,
    onChange,
  } = props;

  // Hooks
  const monthDayYear = useMonthDayYear();
  const { t } = useMEMTranslation();
  const pageName = usePageContext();
  const trackInteraction = useTrackInteraction();
  const availablePartitions = useAvailablePerformancePartitions(selectedParticipationProp, selectedCadenceProp, selectedPartitionGroupProp);
  const [selectedPartition, setSelectedPartition] = useState(selectedPartitionProp);

  // Effects
  useEffect(() => {
    setSelectedPartition(selectedPartitionProp);
  }, [selectedPartitionProp]);

  // This effect ensures that the selected partition is always valid and synchronized with the available partitions.
  // If the current selection is not present in the available list, it tries to select today's partition if possible,
  // or falls back to the last available partition.
  useEffect(() => {
    if (availablePartitions.find((partition) => partition.title === selectedPartition) || availablePartitions.length === 0) return;
    if (availablePartitions.find((partition) => partition.title === selectedPartitionProp)) {
      setSelectedPartition(selectedPartitionProp);
    } else {
      // if there is a partition that wraps todays date, select it
      const today = new Date();
      const todayPartition = availablePartitions.find((partition) => {
        const startDate = new Date(partition.startDate);
        const endDate = new Date(partition.endDate);
        return startDate <= today && endDate >= today;
      });
      if (todayPartition) {
        setSelectedPartition(todayPartition.title);
      } else {
        // else select the last one
        setSelectedPartition(availablePartitions[availablePartitions.length - 1]?.title);
      }
    }
  }, [availablePartitions, selectedPartition, selectedPartitionProp]);

  useEffect(() => {
    onChange(selectedPartition);
  }, [onChange, selectedPartition]);

  // Handlers
  const partitionChanged = (event: any) => {
    if (!event.target.value) return;
    const newPartition = event.target.value;
    trackInteraction('Selected performance partition', pageName, componentName, {
      selectedPerformancePartition: newPartition,
    });
    setSelectedPartition(newPartition);
  };

  return availablePartitions.length > 0 ? (
    <>
      <Combobox label={t('filtersPanelTimePeriodLabel')} value={selectedPartition} onHeChange={partitionChanged}>
        {availablePartitions.map((p) => (
          <Option key={p.title} value={p.title} title={p.title}>
            {`${formatPartition(monthDayYear, p, selectedCadenceProp)}`}
          </Option>
        ))}
      </Combobox>
      <br />
    </>
  ) : null;
});
